import React, { useState, useRef, useEffect } from 'react';
import { Upload, Image as ImageIcon } from 'lucide-react';

import {
  Modal,
  Input,
  FormField,
  Button,
  Divider,
} from "../../design-system";
import { cn } from "../../design-system/utils/cn";

const ImageModal = ({ isOpen, onClose, onInsert }) => {
  const [imageUrl, setImageUrl] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setImageUrl('');
      setIsDragging(false);
    }
  }, [isOpen]);

  const handleClose = () => onClose();

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (imageUrl.trim()) {
      onInsert(imageUrl.trim());
      handleClose();
    }
  };

  // Drag & drop placeholder (upload non implémenté)
  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e) => { e.preventDefault(); setIsDragging(false); };
  const handleFileSelect = (e) => { /* TODO upload */ void e; };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      title="Ajouter une image"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose}>Annuler</Button>
          <Button
            onClick={handleSubmit}
            isDisabled={!imageUrl.trim()}
            leftIcon={<ImageIcon className="w-4 h-4" strokeWidth={1.8} />}
          >
            Insérer l'image
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField label="URL de l'image" helper="Collez l'URL ou entrez-la manuellement.">
          <Input
            type="url"
            placeholder="https://example.com/image.jpg"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            autoFocus
          />
        </FormField>

        <div className="relative flex items-center gap-3">
          <Divider className="flex-1" />
          <span className="text-[10.5px] font-mono uppercase tracking-wider text-mute">ou</span>
          <Divider className="flex-1" />
        </div>

        <FormField label="Glisser-déposer un fichier" helper="⚠️ Drag-and-drop sera implémenté prochainement.">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={cn(
              'flex flex-col items-center justify-center gap-2 py-8 px-4 rounded-md border-2 border-dashed transition-colors text-mute hover:text-text hover:bg-bg-2',
              isDragging ? 'border-ink bg-cream-2/40 text-text' : 'border-border',
            )}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="sr-only"
            />
            <Upload className="w-6 h-6" strokeWidth={1.6} />
            <span className="text-sm font-medium">
              {isDragging ? "Déposez l'image ici" : 'Glissez une image ou cliquez pour sélectionner'}
            </span>
            <span className="text-[11px] font-mono uppercase tracking-wider text-mute">
              PNG · JPG · GIF jusqu'à 10 Mo
            </span>
          </button>
        </FormField>

        {imageUrl && (
          <div>
            <p className="text-[10.5px] font-mono uppercase tracking-wider text-mute mb-2">
              Aperçu
            </p>
            <div className="rounded-md border border-border overflow-hidden bg-bg-2 max-h-48 flex items-center justify-center">
              <img
                src={imageUrl}
                alt="Aperçu"
                className="max-h-48 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div
                style={{ display: 'none' }}
                className="items-center gap-2 px-4 py-6 text-mute"
              >
                <ImageIcon className="w-5 h-5" strokeWidth={1.6} />
                <span className="text-sm italic">Impossible de charger l'image</span>
              </div>
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default ImageModal;
